import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Flag, Search, LogOut, RefreshCw, Calendar, List, Table as TableIcon } from "lucide-react";
import RefereeScoreDialog from "../components/RefereeScoreDialog";

export default function RefereeBoard() {
    const { slug } = useParams();
    const nav = useNavigate();
    const [pin, setPin] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [canchaFilter, setCanchaFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [jornadaFilter, setJornadaFilter] = useState("all");
    const [selected, setSelected] = useState(null);
    const [view, setView] = useState("list");
    const [diaFilter, setDiaFilter] = useState("all");

    const loadData = useCallback(
        async (pinValue) => {
            try {
                const { data } = await api.get(`/referee/${slug}/matches`, {
                    params: { pin: pinValue },
                });
                setData(data);
            } catch (err) {
                toast.error(formatApiError(err.response?.data?.detail));
                localStorage.removeItem("vp_ref");
                sessionStorage.removeItem("vp_ref");
                nav("/arbitro");
            } finally {
                setLoading(false);
            }
        },
        [slug, nav],
    );

    useEffect(() => {
        // Prefer localStorage (persistent across tabs/refresh); fallback to session
        const stored =
            localStorage.getItem("vp_ref") || sessionStorage.getItem("vp_ref");
        if (!stored) {
            nav("/arbitro");
            return;
        }
        try {
            const { pin: p, slug: s } = JSON.parse(stored);
            if (s !== slug) {
                nav("/arbitro");
                return;
            }
            // Mirror to session for fast reads this tab
            sessionStorage.setItem("vp_ref", stored);
            setPin(p);
            loadData(p);
        } catch {
            nav("/arbitro");
        }
    }, [slug, nav, loadData]);

    const matches = data?.matches || [];
    const tournament = data?.tournament;
    const jornadas = tournament?.jornadas || [];

    const scheduled = useMemo(
        () => matches.filter((m) => m.hora && m.cancha && m.status !== "draft"),
        [matches],
    );

    const filteredMatches = useMemo(() => {
        let list = scheduled;
        if (jornadaFilter !== "all") {
            list = list.filter((m) => m.jornada_id === jornadaFilter);
        }
        if (diaFilter !== "all") {
            list = list.filter((m) => String(m.dia || 1) === diaFilter);
        }
        if (canchaFilter !== "all") {
            list = list.filter((m) => String(m.cancha) === canchaFilter);
        }
        if (statusFilter === "pending") {
            list = list.filter((m) => m.status !== "played");
        } else if (statusFilter === "played") {
            list = list.filter((m) => m.status === "played");
        }
        if (q.trim()) {
            const s = q.toLowerCase();
            list = list.filter((m) =>
                [m.local, m.visitante, m.hora, String(m.cancha), m.fase]
                    .filter(Boolean)
                    .some((x) => String(x).toLowerCase().includes(s)),
            );
        }
        return list.slice().sort((a, b) => {
            const da = a.dia || 0;
            const db = b.dia || 0;
            if (da !== db) return da - db;
            const ha = a.hora || "99:99";
            const hb = b.hora || "99:99";
            if (ha !== hb) return ha.localeCompare(hb);
            return (a.cancha || 0) - (b.cancha || 0);
        });
    }, [scheduled, jornadaFilter, diaFilter, canchaFilter, statusFilter, q]);

    const canchas = useMemo(() => {
        const set = new Set(scheduled.map((m) => m.cancha));
        return Array.from(set).sort((a, b) => a - b);
    }, [scheduled]);

    const dias = useMemo(() => {
        const set = new Set(scheduled.map((m) => m.dia).filter(Boolean));
        return Array.from(set).sort((a, b) => a - b);
    }, [scheduled]);

    // Group by jornada (league) or by branch (quick)
    const grouped = useMemo(() => {
        if (filteredMatches.length === 0) return [];
        if (tournament?.kind === "league") {
            const out = [];
            for (const j of jornadas) {
                const jm = filteredMatches.filter((m) => m.jornada_id === j.id);
                if (jm.length > 0) {
                    out.push({
                        key: j.id,
                        label: j.fecha,
                        sublabel: `${j.modo} · ${j.hora_inicio}–${j.hora_fin}`,
                        matches: jm,
                    });
                }
            }
            return out;
        }
        // quick: single group
        return [
            {
                key: "all",
                label: "Partidos",
                sublabel: null,
                matches: filteredMatches,
            },
        ];
    }, [filteredMatches, tournament, jornadas]);

    const logout = () => {
        localStorage.removeItem("vp_ref");
        sessionStorage.removeItem("vp_ref");
        nav("/arbitro");
    };

    if (loading) {
        return (
            <div className="min-h-screen grid place-items-center text-slate-500">
                Cargando…
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            <header className="bg-gradient-to-br from-ipn-900 to-ipn-950 text-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-xl bg-ipnYellow grid place-items-center text-ipn-950 flex-shrink-0">
                            <Flag className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                            <div className="text-xs uppercase tracking-widest text-white/70">
                                Árbitro
                            </div>
                            <h1 className="font-display text-base sm:text-xl font-black truncate">
                                {tournament?.name}
                            </h1>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={() => loadData(pin)}
                            className="rounded-xl bg-white/10 border-white/20 text-white hover:bg-white/20"
                            data-testid="referee-refresh"
                        >
                            <RefreshCw className="w-4 h-4" />
                        </Button>
                        <Button
                            variant="outline"
                            onClick={logout}
                            className="rounded-xl bg-white/10 border-white/20 text-white hover:bg-white/20"
                            data-testid="referee-logout"
                        >
                            <LogOut className="w-4 h-4 sm:mr-1" />
                            <span className="hidden sm:inline">Salir</span>
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
                <div className="vp-card p-3 mb-5 space-y-2">
                    <div className="flex items-center gap-2">
                        <Search className="w-5 h-5 text-slate-400 ml-1 flex-shrink-0" />
                        <Input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Equipo, hora, cancha…"
                            className="border-none shadow-none focus-visible:ring-0 h-11"
                            data-testid="referee-search"
                        />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        {tournament?.kind === "league" && jornadas.length > 0 && (
                            <Select
                                value={jornadaFilter}
                                onValueChange={setJornadaFilter}
                            >
                                <SelectTrigger
                                    className="h-10 rounded-xl text-xs"
                                    data-testid="referee-jornada-filter"
                                >
                                    <SelectValue placeholder="Jornada" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">
                                        Todas las fechas
                                    </SelectItem>
                                    {jornadas.map((j) => (
                                        <SelectItem key={j.id} value={j.id}>
                                            {j.fecha}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                        {tournament?.kind !== "league" && dias.length > 1 && (
                            <Select
                                value={diaFilter}
                                onValueChange={setDiaFilter}
                            >
                                <SelectTrigger
                                    className="h-10 rounded-xl text-xs"
                                    data-testid="referee-dia-filter"
                                >
                                    <SelectValue placeholder="Día" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">
                                        Todos los días
                                    </SelectItem>
                                    {dias.map((d) => (
                                        <SelectItem key={d} value={String(d)}>
                                            Día {d}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                        <Select
                            value={canchaFilter}
                            onValueChange={setCanchaFilter}
                        >
                            <SelectTrigger
                                className="h-10 rounded-xl text-xs"
                                data-testid="referee-cancha-filter"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">
                                    Todas las canchas
                                </SelectItem>
                                {canchas.map((c) => (
                                    <SelectItem key={c} value={String(c)}>
                                        Cancha {c}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select
                            value={statusFilter}
                            onValueChange={setStatusFilter}
                        >
                            <SelectTrigger
                                className="h-10 rounded-xl text-xs"
                                data-testid="referee-status-filter"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos</SelectItem>
                                <SelectItem value="pending">
                                    Pendientes
                                </SelectItem>
                                <SelectItem value="played">Jugados</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {grouped.length === 0 && (
                    <div className="vp-card p-8 text-center text-slate-500">
                        No hay partidos programados con ese filtro.
                    </div>
                )}

                {grouped.length > 0 && (
                    <div className="flex justify-end mb-3">
                        <div
                            className="inline-flex items-center gap-0.5 bg-white border border-slate-200 rounded-xl p-1"
                            data-testid="referee-view-toggle"
                        >
                            <button
                                onClick={() => setView("list")}
                                data-testid="referee-view-list"
                                aria-label="Vista en lista"
                                className={`flex items-center gap-1.5 px-3 h-8 rounded-lg text-xs font-bold transition-colors ${
                                    view === "list"
                                        ? "bg-ipn-800 text-white"
                                        : "text-slate-500 hover:text-ipn-900"
                                }`}
                            >
                                <List className="w-4 h-4" />
                                Lista
                            </button>
                            <button
                                onClick={() => setView("table")}
                                data-testid="referee-view-table"
                                aria-label="Vista en tabla"
                                className={`flex items-center gap-1.5 px-3 h-8 rounded-lg text-xs font-bold transition-colors ${
                                    view === "table"
                                        ? "bg-ipn-800 text-white"
                                        : "text-slate-500 hover:text-ipn-900"
                                }`}
                            >
                                <TableIcon className="w-4 h-4" />
                                Tabla
                            </button>
                        </div>
                    </div>
                )}

                <div className="space-y-6">
                    {grouped.map((g) => (
                        <section key={g.key}>
                            <div className="flex items-center gap-2 mb-2 px-1">
                                <Calendar className="w-4 h-4 text-ipn-800" />
                                <h3 className="font-display text-base sm:text-lg font-black text-ipn-900">
                                    {g.label}
                                </h3>
                                {g.sublabel && (
                                    <span className="text-xs text-slate-500 hidden sm:inline">
                                        · {g.sublabel}
                                    </span>
                                )}
                                <span className="text-xs font-bold text-slate-500 ml-auto">
                                    {g.matches.length}
                                </span>
                            </div>
                            {view === "list" ? (
                                <div className="space-y-2">
                                    {g.matches.map((m) => (
                                        <RefMatchRow
                                            key={m.id}
                                            m={m}
                                            onClick={() => setSelected(m)}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <RefMatchTable
                                    matches={g.matches}
                                    onSelect={setSelected}
                                />
                            )}
                        </section>
                    ))}
                </div>
            </main>

            {selected && (
                <RefereeScoreDialog
                    match={selected}
                    slug={slug}
                    pin={pin}
                    open={!!selected}
                    onOpenChange={(o) => !o && setSelected(null)}
                    onSaved={() => loadData(pin)}
                />
            )}
        </div>
    );
}

function RefMatchRow({ m, onClick }) {
    const played = m.status === "played";
    return (
        <button
            onClick={onClick}
            className={`w-full text-left vp-card vp-card-hover p-3 sm:p-4 ${
                played ? "opacity-80" : ""
            }`}
            data-testid={`referee-match-${m.id}`}
        >
            <div className="flex items-center gap-3">
                <div className="text-center w-14 sm:w-16 flex-shrink-0">
                    {m.dia && (
                        <div className="text-[9px] sm:text-[10px] font-bold uppercase text-ipn-800 bg-ipn-50 rounded px-1 mb-0.5">
                            Día {m.dia}
                        </div>
                    )}
                    <div className="text-[10px] sm:text-xs font-bold uppercase text-slate-400">
                        {m.hora || "—"}
                    </div>
                    <div className="text-base sm:text-lg font-display font-black text-ipn-800">
                        C{m.cancha}
                    </div>
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                        <span
                            className={`px-1.5 py-0.5 rounded ${
                                m.branch === "femenil"
                                    ? "bg-ipn-100 text-ipn-900"
                                    : "bg-slate-800 text-white"
                            }`}
                        >
                            {m.branch}
                        </span>
                        {m.is_friendly && (
                            <span
                                className="px-1.5 py-0.5 rounded bg-amber-500 text-white tracking-widest"
                                data-testid={`friendly-badge-${m.id}`}
                            >
                                AMISTOSO
                            </span>
                        )}
                        <span className="truncate">{m.fase}</span>
                    </div>
                    <div className="mt-1.5 space-y-0.5 text-sm sm:text-base">
                        <div className="font-bold break-words leading-tight flex items-center gap-2">
                            {m.logo_local && (
                                <img
                                    src={m.logo_local}
                                    alt=""
                                    className="w-6 h-6 rounded-full object-cover border border-slate-200 shrink-0"
                                />
                            )}
                            <span>{m.local}</span>
                        </div>
                        <div className="text-slate-400 text-[10px] uppercase font-bold">
                            vs
                        </div>
                        <div className="font-bold break-words leading-tight flex items-center gap-2">
                            {m.logo_vis && (
                                <img
                                    src={m.logo_vis}
                                    alt=""
                                    className="w-6 h-6 rounded-full object-cover border border-slate-200 shrink-0"
                                />
                            )}
                            <span>{m.visitante}</span>
                        </div>
                    </div>
                </div>
                <div className="text-right w-16 sm:w-24 flex-shrink-0">
                    {played ? (
                        <span className="text-sm font-bold bg-emerald-100 text-emerald-800 px-2 py-1 rounded-lg">
                            {m.score}
                        </span>
                    ) : (
                        <span className="text-xs font-bold text-ipn-800">
                            Anotar →
                        </span>
                    )}
                </div>
            </div>
        </button>
    );
}


function RefMatchTable({ matches, onSelect }) {
    return (
        <div className="vp-card overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                    <tr className="text-[10px] uppercase tracking-widest text-slate-500">
                        <th className="text-left px-3 py-2 font-bold">Hora</th>
                        <th className="text-left px-3 py-2 font-bold">C.</th>
                        <th className="text-left px-3 py-2 font-bold">Rama</th>
                        <th className="text-left px-3 py-2 font-bold">Local</th>
                        <th className="text-center px-3 py-2 font-bold">Marcador</th>
                        <th className="text-left px-3 py-2 font-bold">Visitante</th>
                        <th className="text-right px-3 py-2 font-bold">Acción</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                    {matches.map((m) => {
                        const played = m.status === "played";
                        return (
                            <tr
                                key={m.id}
                                className="hover:bg-slate-50 cursor-pointer"
                                onClick={() => onSelect(m)}
                                data-testid={`referee-match-row-${m.id}`}
                            >
                                <td className="px-3 py-2 font-bold whitespace-nowrap">
                                    {m.hora || "—"}
                                </td>
                                <td className="px-3 py-2 font-bold text-ipn-800 whitespace-nowrap">
                                    C{m.cancha}
                                </td>
                                <td className="px-3 py-2">
                                    <span
                                        className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${
                                            m.branch === "femenil"
                                                ? "bg-ipn-100 text-ipn-900"
                                                : "bg-slate-800 text-white"
                                        }`}
                                    >
                                        {m.branch}
                                    </span>
                                </td>
                                <td className="px-3 py-2 font-bold break-words">
                                    {m.local}
                                </td>
                                <td className="px-3 py-2 text-center whitespace-nowrap">
                                    {played ? (
                                        <span className="text-xs font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-md">
                                            {m.score}
                                        </span>
                                    ) : (
                                        <span className="text-[10px] font-bold text-slate-400">
                                            —
                                        </span>
                                    )}
                                </td>
                                <td className="px-3 py-2 font-bold break-words">
                                    {m.visitante}
                                </td>
                                <td className="px-3 py-2 text-right whitespace-nowrap">
                                    <span className="text-xs font-bold text-ipn-800">
                                        {played ? "Editar" : "Anotar"} →
                                    </span>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
